import React, { useEffect, useState } from 'react';
import axios from '../config/api';
import { FaArrowDown, FaArrowUp, FaBitcoin, FaChartLine, FaExclamationCircle, FaOilCan, FaSpinner } from 'react-icons/fa';
import { SiEthereum } from 'react-icons/si';
import {
    formatMarketPrice,
    getMarketSourceLabel,
    getMarketStatusLabel,
    hasMarketPrice,
    MARKET_SUMMARY_ORDER,
    sortMarketSummary
} from '../utils/marketSummary';

const MARKET_CONFIG = {
    XU100: { displayName: 'BIST 100', icon: FaChartLine, color: 'text-red-500', currency: 'TRY' },
    BTC: { displayName: 'Bitcoin', icon: FaBitcoin, color: 'text-orange-500', currency: 'USDT' },
    ETH: { displayName: 'Ethereum', icon: SiEthereum, color: 'text-purple-500', currency: 'USDT' },
    BRENT: { displayName: 'Brent Petrol', icon: FaOilCan, color: 'text-slate-600', currency: 'USD' }
};

const EMPTY_MARKET_DATA = MARKET_SUMMARY_ORDER.map(symbol => ({
    symbol,
    displayName: MARKET_CONFIG[symbol].displayName,
    price: null,
    changePercent: null,
    currency: MARKET_CONFIG[symbol].currency,
    sourceLabel: symbol === 'XU100' ? 'Trunçgil' : ['BTC', 'ETH'].includes(symbol) ? 'Binance' : 'FRED',
    statusLabel: 'Veri yok',
    isAvailable: false,
    isStale: true
}));

const MarketWidget = () => {
    const [marketData, setMarketData] = useState(EMPTY_MARKET_DATA);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get('/api/market-summary');
                const bySymbol = new Map((Array.isArray(data) ? data : []).map(item => [item.symbol, item]));
                setMarketData(sortMarketSummary(EMPTY_MARKET_DATA.map(fallback => ({
                    ...fallback,
                    ...(bySymbol.get(fallback.symbol) || {})
                }))));
                setError(false);
            } catch (requestError) {
                console.error('Piyasa özeti alınamadı:', requestError);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full min-w-0 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 ease-in-out">
            <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800">
                <div className="flex items-center space-x-2">
                    <div className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm transition-colors duration-300 ease-in-out">
                        <FaChartLine className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 dark:text-white">Piyasa Özeti</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Tek fiyat ve değişim</p>
                    </div>
                    {loading && <FaSpinner className="ml-auto animate-spin text-blue-500" />}
                </div>
            </div>

            {error && (
                <div className="mx-3 mt-3 flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 transition-colors duration-300 ease-in-out">
                    <FaExclamationCircle /> Son piyasa verileri alınamadı
                </div>
            )}

            <div className="p-2.5 sm:p-3 space-y-2">
                {marketData.map(item => {
                    const config = MARKET_CONFIG[item.symbol];
                    const Icon = config.icon;
                    const changeAvailable = hasMarketPrice(item) && Number.isFinite(item.changePercent);
                    const isPositive = changeAvailable && item.changePercent >= 0;

                    return (
                        <div key={item.symbol} className="flex items-center justify-between gap-2 p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg min-w-0 transition-colors duration-300 ease-in-out">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                <div className={`w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-600 transition-colors duration-300 ease-in-out ${config.color}`}>
                                    <Icon className="text-lg" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.displayName || config.displayName}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{item.symbol} · {getMarketSourceLabel(item)}</p>
                                    <p className="text-[10px] text-gray-400 dark:text-gray-500">{getMarketStatusLabel(item)}</p>
                                </div>
                            </div>

                            <div className="text-right flex-shrink-0 min-w-0 pl-1 sm:pl-2">
                                <div className="text-xs min-[360px]:text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatMarketPrice(item)}</div>
                                {changeAvailable ? (
                                    <div className={`flex items-center justify-end gap-1 text-xs ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {isPositive ? <FaArrowUp /> : <FaArrowDown />}
                                        <span>{isPositive ? '+' : ''}{item.changePercent.toFixed(2)}%</span>
                                    </div>
                                ) : (
                                    <div className="text-xs text-gray-400">—</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 transition-colors duration-300 ease-in-out">
                <p className="text-[11px] sm:text-xs leading-relaxed text-center text-gray-400 dark:text-gray-500">BIST 5 dk · Kripto 5 dk · Brent günlük</p>
            </div>
        </div>
    );
};

export default MarketWidget;
